import React from 'react'
import PropTypes from 'prop-types'
import classNames from 'classnames'
import deprecated from '../../utils/deprecated'

/**
 * Bronson TileSelect component.
 *
 * @param {boolean} [error] - Error variant of the {@link TileSelect} element.
 * @param {string} [checkboxGroupName] - Set the `[name]` attribute to be treated as a group.
 * @param {string} [radioGroupName] - Set the `[name]` attribute to be treated as a group.
 * @param {('checkbox'|'radio')} [inputType='checkbox'] - Specify the type of the input element.
 * @param {boolean} [checked] - Set the `[checked]` attribute.
 *                              Note: An input element can receive either the `defaultChecked`
 *                              or `checked` props, but not both at the same time.
 * @param {boolean} [defaultChecked] - Set the initial state of the `[checked]` attribute.
 *                                     Note: An input element can receive either the `defaultChecked`
 *                                     or `checked` props, but not both at the same time.
 * @param {string} [icon] - The name of the icon to display.
 * @param {boolean} [iconHiddenOnSmallScreens] - Hide the icon on small screens.
 * @param {string} [iconMeaning] - Text description for the icon.
 * @param {string} [id] - A unique identifier for the input element.
 * @param {string} [value] - Value of the input element.
 * @param {React.ReactNode} [title] - Title heading text.
 * @param {React.ReactNode} [children] - Children to be rendered inside the {@link TileSelect} element.
 * @param {boolean} [disabled] - Set the `[disabled]` and `[aria-disabled]` attributes.
 * @param {string} [buttonText] - Add optional button to the bottom of the {@link TileSelect} element.
 * @param {function} [onClick] - Callback when user clicks on the {@link TileSelect} element.
 * @param {function} [onChange] - Callback when state of the {@link TileSelect} element has changed.
 * @param {string} [testId] - The `testId` string.
 * @param {object} [otherProps] - Other props to pass on the {@link TileSelect} element.
 * @return {JSX.Element} - The {@link TileSelect} element.
 * @constructor
 */
export function TileSelect({
  error,
  checkboxGroupName,
  radioGroupName,
  inputType = 'checkbox',
  checked,
  defaultChecked,
  icon,
  iconHiddenOnSmallScreens,
  iconMeaning,
  id,
  value,
  title,
  children,
  disabled,
  buttonText,
  onClick,
  onChange,
  testId,
  ...otherProps
}) {
  const classNameList = classNames('c-tile-select', {
    'is-error': error,
  }).trim()

  const iconClassNameList = classNames('c-tile-select__icon', 'c-icon', `c-icon--[${icon}]`, {
    'u-hide@xs': iconHiddenOnSmallScreens,
  }).trim()

  const buttonClassNameList = classNames('c-tile-select__button', 'c-btn', {
    'is-disabled': disabled,
  }).trim()

  /**
   * Set the `[name]` value depending on the {@link inputType}.
   * If the type is `radio`, then use `radioGroupName` to ensure that all
   * input elements have the same value to be included in the radio group.
   * In the case of `checkbox`, use the {@link checkboxGroupName} if set to
   * group the TileSelects, otherwise the {@link id} is used.
   * @type {string}
   */
  const inputName = inputType === 'radio' ? radioGroupName : checkboxGroupName ?? id

  /**
   * Callback when user clicks on the {@link TileSelect} element.
   * @type {function}
   */
  const handleClick = (e) => {
    if (onClick) {
      onClick(e)
    }
  }

  /**
   * Callback when state of the {@link TileSelect} element has changed.
   * @type {function}
   */
  const handleChange = (e) => {
    if (onChange) {
      /**
       * Prevents React from resetting its properties.
       * Deprecated: Only relevant for React 16 and earlier. As of React 17,
       * `e.persist()` doesn’t do anything because the `SyntheticEvent` is no longer pooled.
       * @see https://reactjs.org/docs/events.html
       */
      e.persist()
      onChange(e)
    }
  }

  return (
    /**
     * Need to pass `[inert=""]` as React currently does not support HTMLElement.inert mapping.
     * @see https://github.com/facebook/react/pull/24730
     * @TODO: Remove once React supports direct `[inert]` usage.
     */
    <div {...otherProps} data-testid={testId} className={classNameList} inert={disabled ? '' : null}>
      <label className="c-tile-select__label" htmlFor={id}>
        {/* The label is wrapped around and associated via `[for]`. */}
        {/* eslint-disable-next-line jsx-a11y/control-has-associated-label */}
        <input
          className="c-tile-select__input"
          type={inputType}
          id={id}
          value={value}
          name={inputName}
          checked={checked}
          defaultChecked={defaultChecked}
          disabled={disabled}
          aria-disabled={disabled}
          onClick={handleClick}
          onChange={handleChange}
        />
        <span className="c-tile-select__body">
          <span className="c-tile-select__body-top">
            {icon && <i className={iconClassNameList} aria-hidden="true" role="img" />}
            {iconMeaning && <span className="u-visually-hidden">{iconMeaning}</span>}
            {title && <span className="c-tile-select__title">{title}</span>}
            {children && <span className="c-tile-select__text">{children}</span>}
          </span>
          {buttonText && (
            <span className="c-tile-select__body-bottom">
              <span className={buttonClassNameList}>
                <span className="c-btn__text">{buttonText}</span>
              </span>
            </span>
          )}
        </span>
      </label>
    </div>
  )
}

TileSelect.propTypes = {
  error: PropTypes.bool, // Bronson template: 'is-error'.
  checkboxGroupName: PropTypes.string,
  radioGroupName: (props, propName, componentName, ...rest) => {
    if (!props[propName] && props.inputType === 'radio') {
      return new Error('"radioGroupName" needs a value if "inputType" is set to "radio".')
    }
    return PropTypes.string(props, propName, componentName, ...rest)
  }, // Bronson template: 'radio-group-name'.
  inputType: PropTypes.oneOf(['checkbox', 'radio']), // Bronson template: 'input-type'.
  checked: PropTypes.bool, // Bronson template: 'checked'.
  defaultChecked: (props, propName, componentName, ...rest) => {
    if (props[propName] && props.checked !== undefined) {
      return new Error('"defaultChecked" should not be used together with "checked".')
    }
    return PropTypes.bool(props, propName, componentName, ...rest)
  }, // Bronson template: 'checked'.
  icon: PropTypes.string, // Bronson template: 'icon'.
  iconHiddenOnSmallScreens: deprecated(
    PropTypes.bool,
    'Fallback is provided, but will be removed in future versions. Prop will be removed in Bronson-React v6.0.0.'
  ), // Deprecated since Bronson 8.2.0.
  iconMeaning: deprecated(
    PropTypes.string,
    'Icons are decorative and do not need to be communicated to assistive technology. Prop will be removed in Bronson-React v6.0.0.'
  ), // Deprecated since Bronson 8.2.0.
  id: PropTypes.string, // Bronson template: 'id'.
  value: PropTypes.string, // Bronson template: 'value'.
  title: PropTypes.node, // Bronson template: 'title'.
  children: PropTypes.node, // Bronson template: 'text'.
  disabled: PropTypes.bool, // Bronson template: 'disabled'.
  buttonText: PropTypes.string, // Bronson template: 'button-text'.
  onClick: PropTypes.func, // Callback when user clicks on the {@link TileSelect} element.
  onChange: PropTypes.func, // Callback when state of the {@link TileSelect} element has changed.
  testId: PropTypes.string, // Added for `[data-testid]` attribute.
}
